import {TaskListQueryItem} from "../types/task-list-query.item.type";

export const TASKLIST_KEY_TODAY_DIVIDER = 'divider:today';
export const TASKLIST_KEY_FUTURE_DIVIDER = 'divider:future';
export const TASKLIST_KEY_NO_RESULTS = 'notice:no-results';

/** One rendered row of the tasklist: an issue card, a divider, or the no-results notice. */
export type TasklistEntry =
    | { key: string, kind: 'issue', item: TaskListQueryItem }
    | { key: typeof TASKLIST_KEY_TODAY_DIVIDER, kind: 'today' }
    | { key: typeof TASKLIST_KEY_FUTURE_DIVIDER, kind: 'future' }
    | { key: typeof TASKLIST_KEY_NO_RESULTS, kind: 'notice', message: string };

export type TasklistEntries = {
    entries: TasklistEntry[],
    /** Key of the entry "back to today" jumps to: the "today" divider, else the last entry. */
    todayKey: string,
    /** True when no live issue is in the list (only the dividers and the notice are rendered). */
    isEmpty: boolean,
};

/**
 * Builds the rows the tasklist renders from the uid list of the tasklist query.
 *
 * Deleted entries (delivered with `includeDeleted`, only needed to locate a deleted stored anchor)
 * are dropped FIRST, so that a deleted issue with `delta >= 0` cannot move the "today" line and the
 * empty check sees only live issues. Every row has a stable key — the issue uid, or a fixed key for
 * the dividers and the notice — so the block bounds and `repeat()` keep identity across reloads.
 */
export class TasklistEntriesHelper {

    public static build(result: readonly TaskListQueryItem[], noResultsMessage: string): TasklistEntries {
        const live = result.filter(item => item.isDeleted !== true);

        if (live.length === 0) {
            return {
                entries: [
                    {key: TASKLIST_KEY_TODAY_DIVIDER, kind: 'today'},
                    {key: TASKLIST_KEY_NO_RESULTS, kind: 'notice', message: noResultsMessage},
                    {key: TASKLIST_KEY_FUTURE_DIVIDER, kind: 'future'},
                ],
                todayKey: TASKLIST_KEY_TODAY_DIVIDER,
                isEmpty: true,
            };
        }

        const entries: TasklistEntry[] = [];
        let todayPlaced = false;
        let futurePlaced = false;
        for (const item of live) {
            if (!todayPlaced && item.delta >= 0) {
                entries.push({key: TASKLIST_KEY_TODAY_DIVIDER, kind: 'today'});
                todayPlaced = true;
            }
            if (!futurePlaced && item.delta > 0) {
                entries.push({key: TASKLIST_KEY_FUTURE_DIVIDER, kind: 'future'});
                futurePlaced = true;
            }
            entries.push({key: item.uid, kind: 'issue', item});
        }

        return {
            entries,
            // Without entries from today on there is no "today" line; "today" is then the entry
            // closest to it, the last one (as before, `ha-tasklist.view.ts` `_todayDividerIndex`).
            todayKey: todayPlaced ? TASKLIST_KEY_TODAY_DIVIDER : live[live.length - 1].uid,
            isEmpty: false,
        };
    }
}
